import React, { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  EmptyState,
  StatusBadge,
  type StatusTone,
} from '../../ui';
import {
  fetchAgents,
  fetchAgentDrill,
  fetchAttention,
  fetchBudgetAlerts,
  fetchSummary,
} from '../../../lib/api/analytics';
import type {
  AgentHeatmapRow,
  AnalyticsRange,
  BudgetAlertItem,
  FailureMode,
} from '../../../../../shared/types/analytics';

const ATTENTION_REFRESH_MS = 60_000;

// ---------------------------------------------------------------------------
// UX-02 — Reliability tab (moved from pages/health.tsx). AttentionSection
// itself moved to Today (UX-03, not built yet); this panel shows a one-line
// count + link in its place instead of the full attention board.
// ---------------------------------------------------------------------------

export const HealthReliabilityPanel: React.FC<{
  since: AnalyticsRange;
  project: string | undefined;
  setProject: (p: string | undefined) => void;
  account?: string;
}> = ({ since, project, setProject, account }) => {
  return (
    <div data-testid="health-reliability-panel" className="space-y-6">
      <ProjectFilterChip project={project} clear={() => setProject(undefined)} />
      <NeedsAttentionLink account={account} />
      <BudgetAlertsSection account={account} />
      <SummarySection since={since} project={project} account={account} />
      <AgentsSection since={since} project={project} account={account} />
    </div>
  );
};

const ProjectFilterChip: React.FC<{
  project: string | undefined;
  clear: () => void;
}> = ({ project, clear }) =>
  project ? (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-600">Filtered to project:</span>
      <StatusBadge tone="info">
        <span className="font-mono">{project}</span>
      </StatusBadge>
      <button
        type="button"
        onClick={clear}
        className="text-slate-500 hover:text-slate-900"
      >
        clear ✕
      </button>
    </div>
  ) : null;

// ---------------------------------------------------------------------------
// Needs attention — a one-line count + link into Today (UX-03). Reuses the
// same /analytics/attention fetch the old AttentionSection used; Today owns
// the full board now.
// ---------------------------------------------------------------------------

const NeedsAttentionLink: React.FC<{ account?: string }> = ({ account }) => {
  const query = useQuery({
    queryKey: ['analytics', 'attention', account],
    queryFn: () => fetchAttention({ account }),
    refetchInterval: ATTENTION_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  if (query.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (query.isError) {
    return (
      <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
        {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;
  const n = query.data.items.length;
  return (
    <div data-testid="needs-attention-link" className="text-sm text-slate-700">
      <Link to="/today" className="text-primary-600 hover:underline font-medium">
        {n} item{n === 1 ? '' : 's'} need attention → Today
      </Link>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FORGE-111 — Budget alerts (the budget side of the alerts dashboard; the
// Slack/email push signal from the alert-monitor Lambda fires off the same data)
// ---------------------------------------------------------------------------

const BudgetAlertsSection: React.FC<{ account?: string }> = ({ account }) => {
  const query = useQuery({
    queryKey: ['analytics', 'budget-alerts', account],
    queryFn: () => fetchBudgetAlerts({ account }),
    refetchInterval: ATTENTION_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // Healthy budgets are the common case — render nothing rather than an empty card.
  if (query.isSuccess && query.data.items.length === 0) return null;

  return (
    <section data-testid="budget-alerts-section">
      <SectionHeader
        title="Budget alerts"
        description={
          query.data
            ? `${query.data.items.length} project${query.data.items.length === 1 ? '' : 's'} over or near cap (${query.data.month})`
            : 'Projects whose monthly compute crossed the cap or alert threshold.'
        }
      />
      {query.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(query.error as Error).message}
        </div>
      )}
      {query.isSuccess && query.data.items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
          <table className="w-full text-sm" data-testid="budget-alerts-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Severity</Th>
                <Th>Project</Th>
                <Th align="right">Spend</Th>
                <Th align="right">Cap</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((it: BudgetAlertItem) => (
                <tr key={it.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <StatusBadge tone={it.severity === 'critical' ? 'danger' : 'warning'} dot>
                      {it.kind === 'over_budget' ? 'Over budget' : 'Near budget'}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/projects/${encodeURIComponent(it.projectId)}`} className="text-primary-500 hover:underline font-mono text-xs">
                      {it.projectId}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono font-semibold">${it.spendUsd.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono text-slate-700">
                    {it.capUsd != null ? `$${it.capUsd.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{it.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// FR-2 — Summary
// ---------------------------------------------------------------------------

const SummarySection: React.FC<{ since: AnalyticsRange; project?: string; account?: string }> = ({
  since, project, account,
}) => {
  const query = useQuery({
    queryKey: ['analytics', 'summary', since, project, account],
    queryFn: () => fetchSummary({ since, project, account }),
    staleTime: 60_000,
  });
  return (
    <section data-testid="summary-section">
      <SectionHeader title="Success rates" description={`Range: last ${since === 'all' ? '— (all time)' : since}.`} />
      {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {query.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(query.error as Error).message}
        </div>
      )}
      {query.isSuccess && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <RateCard label="Completion" pct={query.data.rates.completionRate} hint={`${query.data.buckets.taskComplete} / ${query.data.buckets.total} sessions`} tone="success" />
          <RateCard label="Force-complete" pct={query.data.rates.forceCompleteRate} hint={`${query.data.buckets.forceComplete} sessions`} tone="warning" />
          <RateCard label="Crash" pct={query.data.rates.crashRate} hint={`${query.data.buckets.crashed} sessions`} tone="danger" />
          <RateCard label="Retry" pct={query.data.rates.retryRate} hint={`${query.data.buckets.retried} / ${query.data.buckets.storyAgentPairs} story-agent pairs`} tone="warning" />
        </div>
      )}
      {query.isSuccess && query.data.source === 'audit' && (
        <p className="text-2xs text-slate-500 mt-2">
          Reading from AUDIT# rows (no TTL) because the range exceeds SESSION's 90-day TTL.
        </p>
      )}
    </section>
  );
};

const RateCard: React.FC<{
  label: string;
  pct: number;
  hint: string;
  tone: StatusTone;
}> = ({ label, pct, hint, tone }) => (
  <Card>
    <CardBody>
      <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-semibold tabular-nums">
          {(pct * 100).toFixed(1)}%
        </span>
        <StatusBadge tone={tone}>{label.toLowerCase()}</StatusBadge>
      </div>
      <div className="text-xs text-slate-500 mt-1">{hint}</div>
    </CardBody>
  </Card>
);

// ---------------------------------------------------------------------------
// FR-4 / FR-5 — Agents + stack
// ---------------------------------------------------------------------------
//
// (FR-3 "Cost by project" was removed from Health — the per-project cost
// view now lives in the Spend tab of the /insights hub; /reports/spend
// redirects there. See UX-02-insights-hub.md.)

const AgentsSection: React.FC<{ since: AnalyticsRange; project?: string; account?: string }> = ({
  since, project, account,
}) => {
  // Deep-linking: stack filter + drill-down cell live in the URL
  // (?stack=node & ?drill=<agent>:<mode>) so a shared link opens the same
  // heatmap slice and drawer (joins the existing since/project params).
  const [searchParams, setSearchParams] = useSearchParams();
  const stack = searchParams.get('stack') ?? undefined;
  const setStack = (next: string | undefined) => {
    const p = new URLSearchParams(searchParams);
    if (!next) p.delete('stack'); else p.set('stack', next);
    setSearchParams(p, { replace: true });
  };
  const query = useQuery({
    queryKey: ['analytics', 'agents', since, project, stack, account],
    queryFn: () => fetchAgents({ since, project, stack, account }),
    staleTime: 60_000,
  });
  const drillParam = searchParams.get('drill');
  const drill: { agent: string; mode: FailureMode } | null = (() => {
    if (!drillParam || !drillParam.includes(':')) return null;
    const idx = drillParam.lastIndexOf(':');
    const agent = drillParam.slice(0, idx);
    const mode = drillParam.slice(idx + 1) as FailureMode;
    if (!agent || !['force_complete', 'crashed', 'high_retries'].includes(mode)) return null;
    return { agent, mode };
  })();
  const setDrill = (next: { agent: string; mode: FailureMode } | null) => {
    const p = new URLSearchParams(searchParams);
    if (!next) p.delete('drill'); else p.set('drill', `${next.agent}:${next.mode}`);
    setSearchParams(p, { replace: true });
  };

  const stackOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of query.data?.stacks ?? []) set.add(r.stack);
    return [...set].sort();
  }, [query.data]);

  return (
    <section data-testid="agents-section">
      <SectionHeader
        title="Agent failure heatmap"
        description="Failure rate by agent. Click a cell to see the underlying SESSION rows."
        right={
          <select
            value={stack ?? ''}
            onChange={(e) => setStack(e.target.value || undefined)}
            aria-label="Filter by stack"
            className="border border-slate-300 rounded-md px-2 py-1 text-xs bg-white"
          >
            <option value="">all stacks</option>
            {stackOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        }
      />
      {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {query.isError && (
        <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
          {(query.error as Error).message}
        </div>
      )}
      {query.isSuccess && query.data.agents.length === 0 && (
        <EmptyState icon="🤖" title="No agent activity in this range yet" />
      )}
      {query.isSuccess && query.data.agents.length > 0 && (
        <AgentMatrix
          rows={query.data.agents}
          onCellClick={(agent, mode) => setDrill({ agent, mode })}
        />
      )}
      {query.isSuccess && query.data.stacks.length > 0 && (
        <div className="mt-5">
          <SectionHeader
            title="Stack failure breakdown"
            description="Slice the same data by backend stack — surfaces stack-specific quality issues."
            dense
          />
          <StackTable rows={query.data.stacks} />
        </div>
      )}

      {drill && (
        <AgentDrillDrawer
          agent={drill.agent}
          mode={drill.mode}
          since={since}
          project={project}
          account={account}
          onClose={() => setDrill(null)}
        />
      )}
    </section>
  );
};

const AgentMatrix: React.FC<{
  rows: AgentHeatmapRow[];
  onCellClick: (agent: string, mode: FailureMode) => void;
}> = ({ rows, onCellClick }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden" data-testid="agent-matrix">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <Th>Agent</Th>
          <Th align="right">Force-complete</Th>
          <Th align="right">Crashed</Th>
          <Th align="right">High retries (≥3)</Th>
          <Th align="right">Total runs</Th>
          <Th align="right">Failure rate</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.agentType} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
            <td className="px-4 py-3 font-medium">{r.agentType}</td>
            <Cell value={r.forceComplete} pct={r.totalRuns > 0 ? r.forceComplete / r.totalRuns : 0}
                  onClick={() => onCellClick(r.agentType, 'force_complete')} />
            <Cell value={r.crashed} pct={r.totalRuns > 0 ? r.crashed / r.totalRuns : 0}
                  onClick={() => onCellClick(r.agentType, 'crashed')} />
            <Cell value={r.highRetries} pct={r.totalRuns > 0 ? r.highRetries / r.totalRuns : 0}
                  onClick={() => onCellClick(r.agentType, 'high_retries')} />
            <td className="px-4 py-3 text-right tabular-nums">{r.totalRuns}</td>
            <td className="px-4 py-3 text-right tabular-nums">
              <span
                className={
                  'inline-block px-2 py-0.5 rounded font-mono ' +
                  pctColor(r.failureRate)
                }
              >
                {(r.failureRate * 100).toFixed(1)}%
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Cell: React.FC<{
  value: number;
  pct: number;
  onClick: () => void;
}> = ({ value, pct, onClick }) => (
  <td className="px-2 py-2 text-right">
    <button
      type="button"
      onClick={onClick}
      disabled={value === 0}
      className={
        'min-w-[64px] px-2 py-1 rounded font-mono text-xs disabled:cursor-default ' +
        pctColor(pct) +
        ' ' +
        (value === 0 ? '' : 'hover:ring-1 hover:ring-primary-300')
      }
      title="Click for SESSION rows"
    >
      {value} {value > 0 && <span className="opacity-70">({(pct * 100).toFixed(0)}%)</span>}
    </button>
  </td>
);

function pctColor(pct: number): string {
  if (pct >= 0.25) return 'bg-danger-50 text-danger-700';
  if (pct >= 0.10) return 'bg-warning-50 text-warning-700';
  return 'bg-success-50 text-success-700';
}

const StackTable: React.FC<{
  rows: { stack: string; agentType: string; totalRuns: number; failures: number; failureRate: number }[];
}> = ({ rows }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <Th>Stack</Th>
          <Th>Agent</Th>
          <Th align="right">Failures</Th>
          <Th align="right">Total</Th>
          <Th align="right">Failure rate</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.stack}::${r.agentType}::${i}`} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
            <td className="px-4 py-2 font-mono text-xs">{r.stack}</td>
            <td className="px-4 py-2">{r.agentType}</td>
            <td className="px-4 py-2 text-right tabular-nums">{r.failures}</td>
            <td className="px-4 py-2 text-right tabular-nums">{r.totalRuns}</td>
            <td className="px-4 py-2 text-right tabular-nums">
              <span className={'inline-block px-2 py-0.5 rounded font-mono ' + pctColor(r.failureRate)}>
                {(r.failureRate * 100).toFixed(1)}%
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ---------------------------------------------------------------------------
// Drill-down drawer
// ---------------------------------------------------------------------------

const AgentDrillDrawer: React.FC<{
  agent: string;
  mode: FailureMode;
  since: AnalyticsRange;
  project?: string;
  account?: string;
  onClose: () => void;
}> = ({ agent, mode, since, project, account, onClose }) => {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['analytics', 'drill', agent, mode, since, project, account],
    queryFn: () => fetchAgentDrill({ agent, mode, since, project, account }),
  });
  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs" onClick={onClose} />
      <aside
        data-testid="agent-drill-drawer"
        className="absolute right-0 top-0 bottom-0 w-full sm:max-w-3xl bg-white shadow-xl border-l border-slate-200 flex flex-col"
      >
        <header className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">
              Sessions
            </div>
            <h2 className="text-base font-semibold text-slate-900">
              {agent}{' '}
              <span className="text-slate-400 font-normal">·</span>{' '}
              <span className="text-sm font-mono">{mode}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 p-1.5"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50">
          {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
          {query.isError && (
            <div role="alert" className="text-sm text-danger-700">{(query.error as Error).message}</div>
          )}
          {query.isSuccess && query.data.rows.length === 0 && (
            <div className="text-sm text-slate-500 italic">No rows for this cell.</div>
          )}
          {query.isSuccess && query.data.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left font-medium pb-1">Epic</th>
                  <th className="text-left font-medium pb-1">Story</th>
                  <th className="text-left font-medium pb-1">Outcome</th>
                  <th className="text-right font-medium pb-1">Cost</th>
                  <th className="text-left font-medium pb-1">Model</th>
                  <th className="text-left font-medium pb-1">Started</th>
                </tr>
              </thead>
              <tbody>
                {query.data.rows.map((r) => (
                  <tr key={r.sessionId} className="border-t border-slate-200">
                    <td className="py-1.5 font-mono">
                      <button
                        type="button"
                        onClick={() => { navigate(`/epics/${r.epicNumber}`); onClose(); }}
                        className="text-primary-500 hover:underline"
                      >
                        #{r.epicNumber}
                      </button>
                      <span className="text-slate-400"> · {r.repo}</span>
                    </td>
                    <td className="py-1.5 font-mono">{r.storyId}</td>
                    <td className="py-1.5">
                      <StatusBadge tone={r.outcome === 'force_complete' ? 'warning' : 'danger'}>
                        {r.outcome ?? '—'}
                      </StatusBadge>
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-mono">${r.costUsd.toFixed(4)}</td>
                    <td className="py-1.5 text-2xs">{r.modelId.replace(/^claude-/, '').replace(/-\d{8}$/, '')}</td>
                    <td className="py-1.5 text-2xs font-mono">{formatTime(r.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

const SectionHeader: React.FC<{
  title: string;
  description?: React.ReactNode;
  right?: React.ReactNode;
  dense?: boolean;
}> = ({ title, description, right, dense }) => (
  <header className={'flex items-end justify-between gap-3 ' + (dense ? 'mb-2' : 'mb-3')}>
    <div>
      <h2 className={(dense ? 'text-sm' : 'text-lg') + ' font-semibold text-slate-900'}>{title}</h2>
      {description && <p className="text-xs text-slate-600 mt-0.5">{description}</p>}
    </div>
    {right}
  </header>
);

const Th: React.FC<{ children?: React.ReactNode; align?: 'left' | 'right' }> = ({
  children,
  align = 'left',
}) => (
  <th
    className={
      'px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold ' +
      (align === 'right' ? 'text-right' : 'text-left')
    }
  >
    {children}
  </th>
);

function formatTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default HealthReliabilityPanel;
